import { reactPlugin, withAITracking } from "../../../../appInsights";
import { Col, Collapse, Row, Select, Spin, Tooltip } from "antd";
import { cloneDeep, isEmpty } from "lodash";
import Icon from "@assets/icon_config";
import "./measure.scss";
import PinUnpin from "@pages/member-details/pin-unpin-component";
import { useEffect, useState } from "react";
import membersService from "@services/members.service";
import DataNotFound from "@components/noDataFound";
import { NO_DATA_FOUND } from "../../../../constants/constants";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import RefreshData from "@components/refreshData/refreshData";
import { useMeasuresStore } from "@stores/measuresStore";

const { Panel } = Collapse;

const renderIcon = (value: number | null, str?: string) => {
  const iconValue = value === 1 ? "g" : value === 0 ? "r" : "";

  switch (iconValue) {
    case "g":
      return str === "flag" ? <Icon name="red-flag" /> : <Icon name="tick-empty" stroke="#1BBE75" />;
    case "r":
      return str === "flag" ? <Icon name="green-flag" /> : <Icon name="cross" />;
    default:
      return <Icon name="minus" />;
  }
};

const Measures = (props: any) => {
  const dataCardId = "39";
  const [activeCardPanel, setActiveCardPanel] = useState<any>(props?.activeKey?.includes(dataCardId) ? ["1"] : []);
  const getHeading = (headerValue: string) => <span>{headerValue}</span>;
  const patientID = membersService?.getCurrentMember?.patient_id;
  const [measures, setMeasures] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [prevPatientId, setPrevPatientId] = useState(patientID); 
  const yearSelectedInCareGaps = useMeasuresStore((state) => state.yearSelectedInCareGaps);
  const setYearSelectedInCareGaps = useMeasuresStore((state) => state.setYearSelectedInCareGaps);
  const comingFromMeasuresReport = useMeasuresStore((state) => state.comingFromMeasuresReport);
  const reportSelectedYear = useMeasuresStore((state) => state.selectedYear);
  const memberStatus = membersService?.getCurrentMember?.patient_status
  const isMemberActive = memberStatus === "Active"

  const currentYear = new Date().getFullYear();
  const startYear = 2024;
  const years = Array.from({ length: currentYear - startYear + 1 }, (_, index) => ({
    id: currentYear - index,
    value: (currentYear - index).toString(),
  }));

  const renderRecords = () => {
    return measures.map((record: any, index: any) => (
      <Row key={index} className="measureContainer__record__row">
        <Col span={13} className="f16 measureContainer__record__col__measureName">
          <span>{record.quality_measure_name}</span>
          <Tooltip
            overlayStyle={{ maxWidth: "500px" }}
            title={
              record.description && (
                <>
                  <span className="f16">{record.description}</span>
                  <br />
                  {<span>Domain: {record.domain}</span>}
                </>
              )
            }
          >
            <span className="pointer measureContainer__record__col__measureName__infoIcon">
              <Icon name="info" colorOfBrand="#7A869A" height={"20"} width={"20"} />
            </span>
          </Tooltip>
        </Col>
        <Col
          span={3}
          className="f14 w400 measureContainer__record__col__other"
          style={{
            color: record.is_eligible === 1 ? "#1BBE75" : "#E75547",
          }}
        >
          {record.is_eligible === 1 ? "Yes" : "No"}
        </Col>
        <Col span={3} className="f14 measureContainer__record__col__other">
          {record.is_eligible === 1 ? renderIcon(record.denominator) : renderIcon(null)}
        </Col>
        <Col span={3} className="f14 measureContainer__record__col__other">
          {record.is_eligible === 1 ? renderIcon(record.numerator) : renderIcon(null)}
        </Col>
        <Col span={2} className="f14 measureContainer__record__col__other">
          <Col span={15} className="measureContainer__record__col__other__icon">
            {record.is_eligible === 1 ? renderIcon(record.gaps, "flag") : renderIcon(null)}
          </Col>
          <Col span={9}>
            {record.quality_measure_id === 8 ? (
              record.is_eligible === 1 && record.gaps === 1 && !record.completedOn ? (
                <Tooltip
                  title={
                    <span>
                      The test result is unavailable or <br /> the test was not conducted
                    </span>
                  }
                >
                  <span className="pointer" style={{ verticalAlign: "text-top" }}>
                    <Icon name="info" colorOfBrand="#7A869A" strokeWidth={"1.2"} height={"19"} width={"18"} />
                  </span>
                </Tooltip>
              ) : record.is_eligible === 1 && record.gaps === 1 && record.completedOn ? (
                <Tooltip
                  title={
                    <>
                      <span className="f16">{`Code - ${record.procedureCode}`}</span>
                      <br />
                      <span className="f16">{`DOS - ${record.completedOn}`}</span>
                    </>
                  }
                >
                  <span className="pointer" style={{ verticalAlign: "text-top" }}>
                    <Icon name="clock" colorOfBrand="#7A869A" strokeWidth={"1"} height={"17"} width={"17"} />
                  </span>
                </Tooltip>
              ) : null
            ) : record.is_eligible === 1 && record.gaps === 0 && record.completedOn ? (
              <Tooltip
                title={
                  <>
                    <span className="f16">{`Code - ${record.procedureCode}`}</span>
                    <br />
                    <span className="f16">{`DOS - ${record.completedOn}`}</span>
                  </>
                }
              >
                <span className="pointer" style={{ verticalAlign: "text-top" }}>
                  <Icon name="clock" colorOfBrand="#7A869A" strokeWidth={"1"} height={"17"} width={"17"} />
                </span>
              </Tooltip>
            ) : null}
          </Col>
        </Col>
      </Row>
    ));
  };

  useEffect(() => {
    setActiveCardPanel(props?.activeKey?.includes(dataCardId) ? ["1"] : []);
  }, [props?.activeKey]);

  const onCollapseChange = () => {
    if (activeCardPanel.length > 0) {
      props?.setActivePanels(cloneDeep(props?.activeKey)?.filter((item: any) => item !== dataCardId));
    } else props?.setActivePanels([...(props?.activeKey ? props?.activeKey : []), dataCardId]);
  };

  const getMeasuresData = (selectedYear?: any) => {
    setLoading(true);
    membersService.getMeasuresInfo(patientID, selectedYear).subscribe({
      next: (res: any) => {
        setMeasures(res.data.data);
        setLoading(false);
      },
      error: (error) => {
        console.log(error);
        setLoading(false);
      },
    });
  };


  useEffect(() => {
    if (comingFromMeasuresReport && reportSelectedYear) {
      if (yearSelectedInCareGaps !== reportSelectedYear) {
        setYearSelectedInCareGaps(reportSelectedYear);
      }
    } else {
      if (yearSelectedInCareGaps !== currentYear.toString()) {
        setYearSelectedInCareGaps(currentYear.toString());
      }
    }
  }, []);
  
  useEffect(() => {
    if (yearSelectedInCareGaps) {
      getMeasuresData(yearSelectedInCareGaps);
    }
  }, [yearSelectedInCareGaps]);
  
  useEffect(() => {
    if (patientID !== prevPatientId) {
      getMeasuresData(yearSelectedInCareGaps);
      setPrevPatientId(patientID);
    }
  }, [patientID]);

  const handleYearChange = (value: string) => {
    setYearSelectedInCareGaps(value);
  };

  const genExtra = (id: any) => (
    <Row onClick={(e) => e.stopPropagation()} align={"middle"}>
      <Select size="large" style={{ width: 130 }} value={yearSelectedInCareGaps} onChange={handleYearChange} onClick={(e) => e.stopPropagation()} className="mr-1">
        {years.map((year) => (
          <Select.Option key={year.id} value={year.value}>
            PY {year.value}
          </Select.Option>
        ))}
      </Select>
      <PinUnpin marginRight={"16px"} dataCardId={id} changeHandler={props?.pinChangeHandler} pinCardIds={props.pinCardIds} />
    </Row>
  );

  return (
    <>
      <Collapse accordion={true} activeKey={activeCardPanel} expandIconPosition="right" bordered={false} onChange={onCollapseChange}>
        <Panel header={getHeading("Measures")} key="1" className="f18 w500 mb-1" extra={genExtra(dataCardId)}>
          {isMemberActive && (
            <Row className="measureContainer__heading f14">
              <Col span={13}>Measure Name</Col>
              <Col span={3} className="measureContainer__heading__center">
                Is Eligible?
                <Tooltip overlayStyle={{ maxWidth: "500px" }} title={"Basic age, gender and enrollment criteria"}>
                  <span className="pointer ml-05" style={{ verticalAlign: "text-top" }}>
                    <Icon name="info" colorOfBrand="#7A869A" height={"15"} width={"15"} />
                  </span>
                </Tooltip>
              </Col>
              <Col span={3} className="measureContainer__heading__center">
                Denominator <br /> Qualified?
              </Col>
              <Col span={3} className="measureContainer__heading__center">
                Numerator <br /> Qualified?
              </Col>
              <Col span={2} className="measureContainer__heading__center">
                Has Gap?
              </Col>
            </Row>
          )}
          {loading ? (
            <Spin spinning={loading} indicator={<HealthemSpinner size={50} />}></Spin>
          ) : !measures || !measures.length ? (
            <DataNotFound message={NO_DATA_FOUND} />
          ) : (
            <div className="measureContainer__record">{renderRecords()}</div>
          )}
          <div className="mt-2">{isMemberActive ? <RefreshData /> : <div className="f14 w400 mt-05">Note: Care gaps are calculated only for active patients.</div>}</div>
        </Panel>
      </Collapse>
    </>
  );
};

export default withAITracking(reactPlugin, Measures);
